import { types } from "@vilocnv/allsetra-core";
import {
  DEFAULT_MAP_FILTERS,
  DEFAULT_MAP_VIEW_PREFERENCES,
  MAP_PREFERENCES_VERSION,
  MAX_FILTER_IDS_PER_FIELD,
  MapPreferencesRecord,
  MapViewPreferences
} from "../types/mapPreferences.types";

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const sanitizeFilterIds = (ids: unknown): string[] => {
  if (!isStringArray(ids)) return [];
  return ids.slice(0, MAX_FILTER_IDS_PER_FIELD);
};

export const sanitizeMapFilters = (
  filters: Partial<types.IMapFilter> | undefined
): types.IMapFilter => ({
  groups: sanitizeFilterIds(filters?.groups),
  objectTypes: sanitizeFilterIds(filters?.objectTypes),
  keyId: sanitizeFilterIds(filters?.keyId)
});

export const buildMapPreferencesCompositeKey = (
  userId: string,
  accountId: string
): string => `${userId}:${accountId}`;

export const parseMapViewPreferences = (
  raw: unknown
): MapViewPreferences | null => {
  if (!raw || typeof raw !== "object") return null;

  const data = raw as Partial<MapViewPreferences>;

  if (
    typeof data.showSatelliteMode !== "boolean" ||
    typeof data.showClusters !== "boolean" ||
    typeof data.showTraffic !== "boolean" ||
    typeof data.showGeozones !== "boolean"
  ) {
    return null;
  }

  return {
    showSatelliteMode: data.showSatelliteMode,
    showClusters: data.showClusters,
    showTraffic: data.showTraffic,
    showGeozones: data.showGeozones,
    filters: sanitizeMapFilters(data.filters)
  };
};

export const parseMapPreferencesRecord = (
  raw: unknown,
  expectedUserId: string,
  expectedAccountId: string
): MapViewPreferences | null => {
  if (!raw || typeof raw !== "object") return null;

  const record = raw as Partial<MapPreferencesRecord>;

  if (
    record.version !== MAP_PREFERENCES_VERSION ||
    record.userId !== expectedUserId ||
    record.accountId !== expectedAccountId
  ) {
    return null;
  }

  return parseMapViewPreferences(record.preferences);
};

export const mergeMapViewPreferences = (
  current: MapViewPreferences,
  partial: Partial<MapViewPreferences>
): MapViewPreferences => ({
  ...current,
  ...partial,
  filters: partial.filters
    ? sanitizeMapFilters(partial.filters)
    : current.filters
});

export const createMapPreferencesRecord = (
  userId: string,
  accountId: string,
  preferences: MapViewPreferences
): MapPreferencesRecord => ({
  version: MAP_PREFERENCES_VERSION,
  userId,
  accountId,
  preferences: {
    ...preferences,
    filters: sanitizeMapFilters(preferences.filters)
  },
  updatedAt: new Date().toISOString()
});

export const getDefaultMapViewPreferences = (): MapViewPreferences => ({
  ...DEFAULT_MAP_VIEW_PREFERENCES,
  // Fresh arrays so consumers cannot mutate the exported DEFAULT_MAP_FILTERS.
  filters: sanitizeMapFilters(DEFAULT_MAP_FILTERS)
});

export type MapTogglePreferences = Pick<
  MapViewPreferences,
  "showSatelliteMode" | "showClusters" | "showTraffic" | "showGeozones"
>;

export const getMapTogglesFromPreferences = (
  preferences: MapViewPreferences
): MapTogglePreferences => ({
  showSatelliteMode: preferences.showSatelliteMode,
  showClusters: preferences.showClusters,
  showTraffic: preferences.showTraffic,
  showGeozones: preferences.showGeozones
});

/** Returns cached preferences or defaults when none exist for this user/account. */
export const resolveMapPreferences = (
  cached: MapViewPreferences | null
): MapViewPreferences =>
  cached
    ? { ...cached, filters: sanitizeMapFilters(cached.filters) }
    : getDefaultMapViewPreferences();

export const getHydratedMapPreferences = (
  cached: MapViewPreferences | null
): { toggles: MapTogglePreferences; filters: types.IMapFilter } => {
  const preferences = resolveMapPreferences(cached);
  return {
    toggles: getMapTogglesFromPreferences(preferences),
    filters: preferences.filters
  };
};
